import { OrderResult } from '@customTypes/order';
import axios from '@hooks/useAxios';

export interface ReportVariant {
  gene: string;
  classification: string;
  variant: string;
  zygosity: string;
  condition: string;
}

export interface ReportPhenotype {
  gene: string;
  diplotype: string;
  phenotype: string;
}

export interface OrderReportDetail {
  clinicalSummary: string | null;
  nextSteps: string[];
  variants: ReportVariant[];
  phenotypes: ReportPhenotype[];
}

const CLASSIFICATION_LABELS: Record<string, string> = {
  PATHOGENIC: 'Pathogenic',
  LIKELY_PATHOGENIC: 'Likely Pathogenic',
  VUS: 'Variant of Unknown Significance',
};

const TYPICAL_PHENOTYPES = new Set(['NORMAL_METABOLIZER', 'NORMAL_FUNCTION', 'VARIANT_ABSENT']);

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const labelFromCode = (value: string, labels: Record<string, string> = {}): string => {
  if (!value) {
    return '';
  }

  if (labels[value]) {
    return labels[value];
  }

  return value
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const resultsRecord = (body: unknown): Record<string, unknown> => {
  const record = asRecord(body);
  if (!record) {
    return {};
  }

  const nested = asRecord(record.results);
  if (
    nested &&
    ('classifications' in nested || 'findings' in nested || 'clinical_summary' in nested || 'next_steps' in nested)
  ) {
    return nested;
  }

  return record;
};

const variantFromClassification = (item: unknown): ReportVariant | null => {
  const record = asRecord(item);
  if (!record) {
    return null;
  }

  const geneRecord = asRecord(record.gene);
  const variation = asRecord(record.variation);
  const inheritance = asRecord(record.inheritance);
  const gene = asString(record.gene_symbol) || asString(geneRecord?.primary_symbol);
  const classification = labelFromCode(asString(record.classification), CLASSIFICATION_LABELS);
  const variant = asString(variation?.display);
  const zygosity = labelFromCode(asString(inheritance?.zygosity) || asString(record.zygosity));
  const condition = asString(record.condition) || asString(record.disease);

  if (!gene && !classification && !variant) {
    return null;
  }

  return { gene, classification, variant, zygosity, condition };
};

const phenotypeFromFinding = (item: unknown): ReportPhenotype | null => {
  const record = asRecord(item);
  if (!record) {
    return null;
  }

  const phenotypeCode = asString(record.gene_phenotype);
  if (!phenotypeCode || TYPICAL_PHENOTYPES.has(phenotypeCode)) {
    return null;
  }

  const genotype = asRecord(record.genotype);
  const gene = asString(genotype?.gene_symbol);
  if (!gene) {
    return null;
  }

  return {
    gene,
    diplotype: asString(genotype?.haplotypes) || asString(record.raw_call),
    phenotype: labelFromCode(phenotypeCode),
  };
};

export const parseOrderReportDetail = (body: unknown): OrderReportDetail => {
  const results = resultsRecord(body);
  const clinicalSummary = asString(results.clinical_summary) || null;
  const nextSteps = Array.isArray(results.next_steps) ? results.next_steps.map(asString).filter(Boolean) : [];
  const variants = Array.isArray(results.classifications)
    ? results.classifications.map(variantFromClassification).filter((item): item is ReportVariant => item !== null)
    : [];
  const phenotypes = Array.isArray(results.findings)
    ? results.findings.map(phenotypeFromFinding).filter((item): item is ReportPhenotype => item !== null)
    : [];

  return { clinicalSummary, nextSteps, variants, phenotypes };
};

export const reportDetailIsEmpty = (detail: OrderReportDetail): boolean =>
  !detail.clinicalSummary && !detail.nextSteps.length && !detail.variants.length && !detail.phenotypes.length;

const jsonReportFile = (files: OrderResult[] | undefined): OrderResult | undefined =>
  files?.find(file => file.mimetype === 'application/json' && file.uri);

// Natera partner next steps live on the downloaded report. result_json only has the MyOme steps.
const partnerNextSteps = async (jsonFile: OrderResult): Promise<string[] | null> => {
  const response = await fetch(jsonFile.uri);
  if (!response.ok) {
    return null;
  }

  const steps = parseOrderReportDetail(await response.json()).nextSteps;
  return steps.length ? steps : null;
};

export const fetchOrderReportDetail = async (orderId: string): Promise<OrderReportDetail> => {
  const { data } = await axios.get(`/order/${orderId}`, { params: { expand: true } });
  const detail = parseOrderReportDetail(data?.result_json);
  const partnerFiles = Array.isArray(data?.myome_results) && data.myome_results.length ? data.results : undefined;
  const partnerJson = jsonReportFile(partnerFiles);
  if (partnerJson) {
    const steps = await partnerNextSteps(partnerJson);
    if (steps) {
      detail.nextSteps = steps;
    }
  }

  return detail;
};
